import { useState, useCallback, useEffect } from "react";

/**
 * useScrollZone
 * -------------
 *  - ratio : part haute & basse (0-0,49). 0,35 = 35 %.
 *  - minGapPx : si la zone centrale est plus petite, on ne montre aucun bouton.
 *
 * Renvoie : "TOP" | "MIDDLE" | "BOTTOM"
 */
export function useScrollZones(ref, ratio = 0.35, minGapPx = 500) {
  const safeRatio = Math.min(ratio, 0.49);                 // éviter le chevauchement
  const [zone, setZone] = useState("MIDDLE");

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight - clientHeight;        // distance totale

    // Aucune barre de scroll → aucun bouton
    if (scrollable <= 0) {
      setZone("MIDDLE");
      return;
    }

    const threshold = scrollable * safeRatio;              // 35 % du scroll total
    const gap = scrollable - 2 * threshold;                // 30 % restant

    // Si la zone centrale est < 500 px, on désactive les boutons
    if (gap < minGapPx) {
      setZone("MIDDLE");
      return;
    }

    // Logique habituelle
    if (scrollTop <= threshold) {
      setZone("TOP");            // bouton « Bas ↓ »
    } else if (scrollTop >= scrollable - threshold) {
      setZone("BOTTOM");         // bouton « ↑ Haut »
    } else {
      setZone("MIDDLE");         // aucun bouton
    }
  }, [ref, safeRatio, minGapPx]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    check();                                         // état initial
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [check]);

  return zone;                                       // "TOP" | "MIDDLE" | "BOTTOM"
}
